/**
 * @engine/vfx - Initialize Context Nodes
 *
 * Nodes for setting initial particle attributes.
 */

import type { VFXNodeDefinition } from '../types';
import { dataPort, flowPort } from '../types';

/**
 * Initialize context node definitions.
 */
export const INIT_NODES: readonly VFXNodeDefinition[] = [
  // ---- Set Lifetime ----
  {
    id: 'vfx.init.lifetime',
    name: 'Set Lifetime',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set particle lifetime',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('lifetime', 'Lifetime', 'float', { defaultValue: 1, range: { min: 0, max: 60 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.maxLifetime = lifetime;`,
    },
  },

  // ---- Set Lifetime Random ----
  {
    id: 'vfx.init.lifetime_random',
    name: 'Set Lifetime Random',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set particle lifetime with random range',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('min', 'Min', 'float', { defaultValue: 0.5, range: { min: 0, max: 60 } }),
      dataPort('max', 'Max', 'float', { defaultValue: 2, range: { min: 0, max: 60 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.maxLifetime = lerp(min, max, hash(particle.seed + 10u));`,
    },
  },

  // ---- Position: Shape ----
  {
    id: 'vfx.init.position_shape',
    name: 'Position (Shape)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set initial position from a shape',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('shape', 'Shape', 'int', {
        defaultValue: 0,
        options: ['Point', 'Circle', 'Sphere', 'Box', 'Line', 'Mesh'],
      }),
      dataPort('radius', 'Radius', 'float', { defaultValue: 1, range: { min: 0, max: 100 } }),
      dataPort('size', 'Size', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('arc', 'Arc', 'float', { defaultValue: 360, range: { min: 0, max: 360 } }),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        // Shape-based position (computed based on shape type)
        particle.position = computeShapePosition(shape, radius, size, arc, center, particle.seed);
      `,
    },
  },

  // ---- Position: Line ----
  {
    id: 'vfx.init.position_line',
    name: 'Position (Line)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set position along a line',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('start', 'Start', 'vec3', { defaultValue: { x: -1, y: 0, z: 0 } }),
      dataPort('end', 'End', 'vec3', { defaultValue: { x: 1, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.position = lerp(start, end, hash(particle.seed + 20u));`,
    },
  },

  // ---- Set Position ----
  {
    id: 'vfx.init.set_position',
    name: 'Set Position',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set particle position from input (use with operators)',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('position', 'Position', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.position = position;`,
    },
  },

  // ---- Velocity: Random Direction ----
  {
    id: 'vfx.init.velocity_random',
    name: 'Velocity (Random)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set random velocity direction',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('speed', 'Speed', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
      dataPort('speedVariance', 'Variance', 'float', { defaultValue: 1, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dir = normalize(hash3(particle.seed + 60u) * 2.0 - 1.0);
        let spd = speed + (hash(particle.seed + 63u) * 2.0 - 1.0) * speedVariance;
        particle.velocity = dir * spd;
      `,
    },
  },

  // ---- Velocity: Direction ----
  {
    id: 'vfx.init.velocity_direction',
    name: 'Velocity (Direction)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set velocity in a specific direction with spread',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('direction', 'Direction', 'vec3', { defaultValue: { x: 0, y: 1, z: 0 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
      dataPort('spread', 'Spread', 'float', { defaultValue: 15, range: { min: 0, max: 180 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dirLen = length(direction);
        let safeDir = (dirLen > 0.0001 ? direction / dirLen : float3(0.0, 1.0, 0.0));
        let dir = applySpread(safeDir, spread, particle.seed + 60u);
        particle.velocity = dir * speed;
      `,
    },
  },

  // ---- Velocity: From Center ----
  {
    id: 'vfx.init.velocity_from_center',
    name: 'Velocity (From Center)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set velocity pointing away from center',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dir = normalize(particle.position - center);
        particle.velocity = dir * speed;
      `,
    },
  },

  // ---- Set Color ----
  {
    id: 'vfx.init.color',
    name: 'Set Color',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set initial particle color',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('color', 'Color', 'color', { defaultValue: { r: 1, g: 1, b: 1, a: 1 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.color = color;`,
    },
  },

  // ---- Set Color Random ----
  {
    id: 'vfx.init.color_random',
    name: 'Set Color Random',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set random color from gradient',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('gradient', 'Gradient', 'gradient', { defaultValue: null }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.color = gradient(hash(particle.seed + 40u));`,
    },
  },

  // ---- Set Size ----
  {
    id: 'vfx.init.size',
    name: 'Set Size',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set initial particle size',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('size', 'Size', 'float', { defaultValue: 1, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.size = size;`,
    },
  },

  // ---- Set Size Random ----
  {
    id: 'vfx.init.size_random',
    name: 'Set Size Random',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set random particle size',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('min', 'Min', 'float', { defaultValue: 0.5, range: { min: 0, max: 100 } }),
      dataPort('max', 'Max', 'float', { defaultValue: 1.5, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.size = lerp(min, max, hash(particle.seed + 30u));`,
    },
  },

  // ---- Set Scale ----
  {
    id: 'vfx.init.scale',
    name: 'Set Scale',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set initial particle scale (non-uniform)',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('scaleX', 'Scale X', 'float', { defaultValue: 1, range: { min: 0.01, max: 10 } }),
      dataPort('scaleY', 'Scale Y', 'float', { defaultValue: 1, range: { min: 0.01, max: 10 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.scale = float2(scaleX, scaleY);`,
    },
  },

  // ---- Set Scale Random ----
  {
    id: 'vfx.init.scale_random',
    name: 'Set Scale Random',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set random particle scale',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('minX', 'Min X', 'float', { defaultValue: 0.5, range: { min: 0.01, max: 10 } }),
      dataPort('maxX', 'Max X', 'float', { defaultValue: 1.5, range: { min: 0.01, max: 10 } }),
      dataPort('minY', 'Min Y', 'float', { defaultValue: 0.5, range: { min: 0.01, max: 10 } }),
      dataPort('maxY', 'Max Y', 'float', { defaultValue: 1.5, range: { min: 0.01, max: 10 } }),
      dataPort('uniform', 'Uniform', 'bool', { defaultValue: false }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let rx = hash(particle.seed + 31u);
        let ry = (uniform ? rx : hash(particle.seed + 32u));
        particle.scale = float2(lerp(minX, maxX, rx), lerp(minY, maxY, ry));
      `,
    },
  },

  // ---- Set Rotation ----
  {
    id: 'vfx.init.rotation',
    name: 'Set Rotation',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set initial particle rotation',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('angle', 'Angle', 'float', { defaultValue: 0, range: { min: 0, max: 360 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.rotation = radians(angle);`,
    },
  },

  // ---- Set Angular Velocity ----
  {
    id: 'vfx.init.angular_velocity',
    name: 'Set Angular Velocity',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set particle spin speed',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('speed', 'Speed', 'float', { defaultValue: 0, range: { min: -360, max: 360 } }),
      dataPort('variance', 'Variance', 'float', { defaultValue: 0, range: { min: 0, max: 360 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        particle.angularVelocity = radians(speed + (hash(particle.seed + 50u) * 2.0 - 1.0) * variance);
      `,
    },
  },

  // ---- Set Attribute ----
  {
    id: 'vfx.init.set_attribute',
    name: 'Set Attribute',
    category: 'attribute',
    contexts: ['initialize'],
    description: 'Set a custom particle attribute',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('attribute', 'Attribute', 'int', { defaultValue: 0 }),
      dataPort('value', 'Value', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
  },

  // ---- Velocity: Cone ----
  {
    id: 'vfx.init.velocity_cone',
    name: 'Velocity (Cone)',
    category: 'source',
    contexts: ['initialize'],
    description: 'Spawn particles in a cone shape',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('direction', 'Direction', 'vec3', { defaultValue: { x: 0, y: 1, z: 0 } }),
      dataPort('angle', 'Angle', 'float', { defaultValue: 30, range: { min: 0, max: 180 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
      dataPort('speedVariance', 'Speed Variance', 'float', { defaultValue: 1, range: { min: 0, max: 50 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dirNorm = normalize(direction);

        // Generate random point on unit sphere
        let theta = hash(particle.seed + 70u) * 6.28318;
        let cosAngle = 1.0 - hash(particle.seed + 71u) * (1.0 - cos(radians(angle)));
        let sinAngle = sqrt(1.0 - cosAngle * cosAngle);

        // Create basis vectors perpendicular to direction
        var up = float3(0.0, 1.0, 0.0);
        if (abs(dot(dirNorm, up)) > 0.99) {
          up = float3(1.0, 0.0, 0.0);
        }
        let right = normalize(cross(dirNorm, up));
        let forward = cross(right, dirNorm);

        // Construct cone direction
        let coneDir = dirNorm * cosAngle + (right * cos(theta) + forward * sin(theta)) * sinAngle;

        let spd = speed + (hash(particle.seed + 72u) * 2.0 - 1.0) * speedVariance;
        particle.velocity = normalize(coneDir) * spd;
      `,
    },
  },

  // ---- Velocity: Inherit ----
  {
    id: 'vfx.init.inherit_velocity',
    name: 'Inherit Velocity',
    category: 'source',
    contexts: ['initialize'],
    description: 'Inherit velocity from emitter or parent',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('multiplier', 'Multiplier', 'float', { defaultValue: 1, range: { min: 0, max: 5 } }),
      dataPort('additive', 'Additive', 'bool', { defaultValue: true }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        // emitterVelocity is a uniform set by the runtime
        let inherited = emitterVelocity * multiplier;
        if (additive) {
          particle.velocity += inherited;
        } else {
          particle.velocity = inherited;
        }
      `,
    },
  },

  // ---- Position: Noise Offset ----
  {
    id: 'vfx.init.noise_offset',
    name: 'Noise Offset',
    category: 'source',
    contexts: ['initialize'],
    description: 'Add noise-based offset to position',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('amplitude', 'Amplitude', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('frequency', 'Frequency', 'float', { defaultValue: 1, range: { min: 0.01, max: 10 } }),
      dataPort('seed', 'Seed', 'float', { defaultValue: 0, range: { min: 0, max: 1000 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let noiseInput = particle.position * frequency + float3(seed);
        let noiseVal = float3(
          snoise3D(noiseInput),
          snoise3D(noiseInput + float3(100.0, 0.0, 0.0)),
          snoise3D(noiseInput + float3(0.0, 100.0, 0.0))
        );
        particle.position += noiseVal * amplitude;
      `,
    },
  },

  // ============================================================================
  // POINT MAP SOURCES
  // ============================================================================

  // ---- Position from Point Map ----
  {
    id: 'vfx.init.position_from_point_map',
    name: 'Position from Point Map',
    category: 'source',
    contexts: ['initialize'],
    description: 'Set initial position from a point map texture',
    color: '#2196F3',
    inputs: [
      flowPort('exec', ''),
      dataPort('pointMap', 'Point Map', 'texture2d'),
      dataPort('pointCount', 'Point Count', 'uint', { defaultValue: 0 }),
      dataPort('sampleMode', 'Sample Mode', 'int', { defaultValue: 0, options: ['Sequential', 'Random', 'RandomConstant'] }),
      dataPort('addressMode', 'Address Mode', 'int', { defaultValue: 0, options: ['Wrap', 'Clamp', 'Mirror'] }),
      dataPort('scale', 'Scale', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('bias', 'Bias', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let pmDims = textureSize(pointMap);
        let pmTexSize = pmDims.x * pmDims.y;
        let pmCount = (pointCount > 0u ? min(pointCount, pmTexSize) : pmTexSize);

        // Determine sample index based on mode
        uint pmIdx;
        if (sampleMode == 0) {
          pmIdx = idx;
        } else if (sampleMode == 1) {
          pmIdx = uint(hash(particle.seed) * float(pmCount));
        } else {
          pmIdx = uint(hash(particle.seed + 12345u) * float(pmCount));
        }

        // Apply addressing mode
        if (addressMode == 0) {
          pmIdx = pmIdx % pmCount;
        } else if (addressMode == 1) {
          pmIdx = min(pmIdx, pmCount - 1u);
        } else {
          let pmCycle = pmIdx / pmCount;
          let pmPos = pmIdx % pmCount;
          pmIdx = ((pmCycle & 1u) == 1u ? pmCount - 1u - pmPos : pmPos);
        }

        let pmY = pmIdx / pmDims.x;
        let pmX = pmIdx - (pmY * pmDims.x);
        let pmTexel = pointMap.Load(int3(int(pmX), int(pmY), 0));
        particle.position = (pmTexel.xyz + bias) * scale;
      `,
    },
  },

  // ---- Set Color from Point Map ----
  {
    id: 'vfx.init.color_from_point_map',
    name: 'Set Color from Point Map',
    category: 'source',
    contexts: ['initialize', 'update'],
    description: 'Set particle color from a point map texture',
    color: '#E91E63',
    inputs: [
      flowPort('exec', ''),
      dataPort('colorMap', 'Color Map', 'texture2d'),
      dataPort('pointCount', 'Point Count', 'uint', { defaultValue: 0 }),
      dataPort('sampleMode', 'Sample Mode', 'int', { defaultValue: 0, options: ['Sequential', 'Random', 'RandomConstant'] }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let cmDims = textureSize(colorMap);
        let cmTexSize = cmDims.x * cmDims.y;
        let cmCount = (pointCount > 0u ? min(pointCount, cmTexSize) : cmTexSize);

        uint cmIdx;
        if (sampleMode == 0) {
          cmIdx = idx % cmCount;
        } else if (sampleMode == 1) {
          cmIdx = uint(hash(particle.seed) * float(cmCount));
        } else {
          cmIdx = uint(hash(particle.seed + 12345u) * float(cmCount));
        }

        let cmY = cmIdx / cmDims.x;
        let cmX = cmIdx - (cmY * cmDims.x);
        particle.color = colorMap.Load(int3(int(cmX), int(cmY), 0));
      `,
    },
  },
];
